import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendUp, TrendDown, CalendarBlank, CurrencyInr, ShoppingCart, ChartLine } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ClientForecast from '../components/ClientForecast';
import { BACKEND_URL } from '../config';

const ClientDrillDown = ({ clientId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(async () => {
    try {
      const { data: result } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/detail`, { withCredentials: true });
      setData(result);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading client details...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">Client not found</div>;

  const { client, total_revenue, total_transactions, avg_monthly_revenue, growth_percentage, active_months, chart_data, recent_sales } = data;

  const formatMonth = (label) => {
    if (!label) return '';
    const [y, m] = label.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m)-1]} '${y.slice(2)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" className="rounded-sm" onClick={onBack} data-testid="drilldown-back-btn">
          <ArrowLeft size={16} weight="bold" className="mr-1" /> Back
        </Button>
        <div>
          <div className="text-2xl font-bold tracking-tight" data-testid="drilldown-client-name">{client.name}</div>
          <div className="text-sm text-muted-foreground">{[client.company, client.industry, client.region].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-sm p-4" data-testid="drilldown-total-revenue">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyInr size={16} weight="regular" className="text-muted-foreground" />
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Total Revenue</span>
          </div>
          <div className="text-xl font-bold tracking-tight">₹{total_revenue.toLocaleString()}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="drilldown-transactions">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart size={16} weight="regular" className="text-muted-foreground" />
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Transactions</span>
          </div>
          <div className="text-xl font-bold tracking-tight">{total_transactions}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="drilldown-avg-monthly">
          <div className="flex items-center gap-2 mb-2">
            <CalendarBlank size={16} weight="regular" className="text-muted-foreground" />
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Avg Monthly</span>
          </div>
          <div className="text-xl font-bold tracking-tight">₹{avg_monthly_revenue.toLocaleString()}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="drilldown-growth">
          <div className="flex items-center gap-2 mb-2">
            {growth_percentage !== null && growth_percentage >= 0
              ? <TrendUp size={16} weight="bold" className="text-accent" />
              : <TrendDown size={16} weight="bold" className="text-destructive" />}
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Growth</span>
          </div>
          <div className={`text-xl font-bold tracking-tight ${growth_percentage !== null && growth_percentage >= 0 ? 'text-accent' : 'text-destructive'}`}>
            {growth_percentage !== null ? `${growth_percentage > 0 ? '+' : ''}${growth_percentage}%` : '—'}
          </div>
          <div className="text-xs text-muted-foreground">{active_months} active months</div>
        </div>
      </div>

      {/* Trend chart */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <ChartLine size={18} weight="regular" />
          <span className="font-semibold text-sm">Revenue Trend</span>
        </div>
        {chart_data.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tickFormatter={formatMonth} stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} />
              <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '12px' }} />
              <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={{ fill: 'hsl(var(--accent))', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex items-center justify-center text-muted-foreground">No sales data</div>
        )}
      </div>

      {/* Forecast */}
      <ClientForecast clientId={clientId} clientName={client.name} />

      {/* Recent sales */}
      <div className="bg-card border border-border rounded-sm">
        <div className="p-4 border-b border-border font-semibold text-sm">Recent Transactions</div>
        {recent_sales.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No transactions recorded</div>
        ) : (
          <div className="divide-y divide-border">
            {recent_sales.map((sale) => (
              <div key={sale.id} data-testid={`drilldown-sale-${sale.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
                <div>
                  <div className="text-sm font-medium">₹{sale.amount.toLocaleString()}</div>
                  {sale.description && <div className="text-xs text-muted-foreground">{sale.description}</div>}
                </div>
                <div className="text-xs text-muted-foreground">{new Date(sale.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDrillDown;
